/**
 * Utility functions for formatting and processing text
 */

/**
 * Format a date object or timestamp into a human-readable string
 * @param {Date|number|string} date - Date object, timestamp, or string date
 * @param {boolean} includeTime - Whether to include the time in the formatted date
 * @returns {string} Formatted date string
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'object' ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
    
    return dateObj.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Extract sections from a markdown-formatted text
 * @param {string} text - The text to extract sections from
 * @returns {Object} Object with section titles as keys and content as values
 */
export const extractSections = (text) => {
  if (!text) return {};
  
  const sections = {};
  const lines = text.split('\n');
  let currentSection = null;
  let currentContent = [];
  
  // Look for Markdown headings (## Section Title)
  // or numerical sections (1. Section Title)
  const sectionPattern = /^(#+\s+|(?:\d+\.)\s+)(.+)$/;
  
  lines.forEach(line => {
    const match = line.match(sectionPattern);
    
    if (match) {
      // If we were building a previous section, save it
      if (currentSection && currentContent.length) {
        sections[currentSection] = currentContent.join('\n');
      }
      
      // Start a new section
      currentSection = match[2].trim();
      currentContent = [];
    } else if (currentSection) {
      // Add this line to the current section
      currentContent.push(line);
    }
  });
  
  // Save the last section if there is one
  if (currentSection && currentContent.length) {
    sections[currentSection] = currentContent.join('\n');
  }
  
  return sections;
};

/**
 * Format a number with commas for thousands
 * @param {number|string} number - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number
 */
export const formatNumber = (number, decimals = 2) => {
  if (number === null || number === undefined || number === '') return '';
  
  try {
    const num = typeof number === 'string' ? parseFloat(number.replace(/[^0-9.-]+/g, '')) : number;
    
    if (isNaN(num)) return '';
    
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  } catch (error) {
    console.error('Error formatting number:', error);
    return number.toString();
  }
};

/**
 * Extract a price from a string (looking for $ followed by numbers)
 * @param {string} text - Text to extract price from
 * @returns {string|null} Extracted price or null if not found
 */
export const extractPrice = (text) => {
  if (!text) return null;
  
  const priceMatch = text.match(/\$\s*(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/);
  return priceMatch ? priceMatch[0] : null;
};

/**
 * Extract a percentage change from a string
 * @param {string} text - Text to extract percentage from
 * @returns {string|null} Extracted percentage or null if not found
 */
export const extractPercentage = (text) => {
  if (!text) return null;
  
  const percentMatch = text.match(/(?:[-+]\s*\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?\s*%)|(?:[-+]\s*\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?\s*percent)/i);
  return percentMatch ? percentMatch[0] : null;
}; 